import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AppCheckBox,
  AppFormikButtonStateTiles,
  AppFormikDatePicker,
  AppFormikSelect,
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { ReactComponent as Change } from 'assets/images/ic_change.svg';
import { ReactComponent as ChevronDown } from 'assets/images/ic_chevron_down.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { AISolutionsScheme } from 'validators/AISolutionsScheme';
import { AISystemsService } from 'services/AISystemService';
import { Toast } from 'shared/helpers';
import PageHeader from 'shared/components/PageHeader';
import {
  INTERNAL_TYPE_LABEL,
  INTERNAL_TYPES,
  THIRD_PARTY_TYPE_LABEL,
  THIRD_PARTY_TYPES,
} from 'shared/Constants';
import { ReactComponent as Home } from 'assets/images/ic_home.svg';
import { ReactComponent as Globe } from 'assets/images/ic_globe.svg';
import ScrollToError from 'shared/components/ScrollToError';

export const TechnicalFields = [
  'platform',
  'risktier',
  'opensource',
  'aimodeltypes',
  'technicaldocument',
  'vendorname',
  'consumtionmethod',
  'environment',
  'watermarkingtechnique',
  'lifecyclestage',
  'versioncontrol',
  'selectcapability',
  'builddate',
  'trainingdate',
];
const FormSourceTypes = ({ purpose = 'new' }): ReactElement => {
  const [businessUsageStateOpen, setBusinessUsageOpen] =
    useState<boolean>(true);
  const location = useLocation();
  const navigation = useNavigate();
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('md'));
  const { aiSystemId } = useParams();
  const [sourceType, setSourceType] = useState('internal');
  const [internalType, setInternalType] = useState(0);
  const [thirdPartyType, setThirdPartyType] = useState(0);
  const [techGovStateOpen, setTechGovOpen] = useState<boolean>(false);
  const [showChangeType, setShowChangeType] = useState<boolean>(false);
  const [systemDetail, setSystemDetail] = useState<any>(
    location.state?.systemDetail || null
  );
  const sourceTypeTitle = useMemo(
    () =>
      sourceType === 'internal'
        ? INTERNAL_TYPE_LABEL[internalType]
        : THIRD_PARTY_TYPE_LABEL[thirdPartyType],
    [sourceType, internalType, thirdPartyType]
  );
  const [technicalFieldsToShow, setTechnicalFieldsToShow] =
    useState(TechnicalFields);

  // Instance Methods
  const loadSourceType = (): void => {
    const iIndex = INTERNAL_TYPES.findIndex(
      (f) => f.title === systemDetail.sourceType
    );
    console.log('loadSourceType', systemDetail);
    if (iIndex > -1) {
      setSourceType('internal');
      setInternalType(iIndex + 1);
    }
    const tIndex = THIRD_PARTY_TYPES.findIndex(
      (f) => f.title === systemDetail.sourceType
    );
    if (tIndex > -1) {
      setSourceType('third-party');
      setThirdPartyType(tIndex + 1);
    }
  };

  const handleCloseChangeType = (reset: boolean): void => {
    setShowChangeType(false);
    const container = document.querySelector('.app-right-container');
    container?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (reset) {
      loadSourceType();
    }
  };

  // UI Actions
  const handleFormSourceType = (values: any): void => {
    const payload = {
      solutionName: values.solutionName,
      solutionOwner: values.solutionOwner,
      organizationUnit: values.organizationUnit,
      organizationSubUnit: values.organizationSubUnit,
      stakeholder: values.stakeholder,
      legalEntity: values.legalentity,
      userCustomerName: values.usercustomer,
      businessProcesses: values.businessprocess,
      uses: values.uses,
      purpose: values.purpose,
      businessValue: values.businessValue,
      businessGoals: values.businessGoals,
      platformName: values.platform,
      riskTier: values.risktier,
      technicalDocumentationLink: values.technicaldocument,
      vendorName: values.vendorname,
      consumptionMethod: values.consumtionmethod,
      environment: values.environment,
      versionControlMethod: values.versioncontrol,
      decisionCapability: values.selectcapability,
      watermarkingTechnique: values.watermarkingtechnique,
      lifecycleStage: values.lifecyclestage,
      aiModelTypes: values.aimodeltypes,
      lastTrainingDate: values.trainingdate,
      buildDate: values.builddate,
      openSource: values.opensource.some((t: any, i: number) => i === 1 && t),
      approvalStatus: 'Pending',
      classification: 'High Risk',
      sourceType: sourceTypeTitle,
      organizationId: values?.organizationId,
      createdBy: null,
      updatedBy: null,
      isConverted: true,
    };

    const request =
      purpose === 'edit'
        ? AISystemsService.updateAISystems(aiSystemId, payload)
        : AISystemsService.createAISystems(payload);

    request
      .then(() => {
        Toast.showSuccessMessage(
          `Successfully ${
            purpose === 'edit' ? 'updated' : 'created'
          } AI Solution`
        );
        navigation('/ai-systems');
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };
  // Hooks
  useEffect(() => {
    const { thirdPartyType } = location.state;

    if (thirdPartyType === 1) {
      setTechnicalFieldsToShow([
        'risktier',
        'opensource',
        'technicaldocument',
        'vendorname',
        'watermarkingtechnique',
      ]);
    }
    if (thirdPartyType === 2) {
      setTechnicalFieldsToShow([
        'risktier',
        'opensource',
        'technicaldocument',
        'vendorname',
        'watermarkingtechnique',
        'selectcapability',
      ]);
    }
    if (thirdPartyType === 3) {
      setTechnicalFieldsToShow([
        'risktier',
        'opensource',
        'technicaldocument',
        'vendorname',
        'environment',
      ]);
    }
    if (thirdPartyType === 4) {
      setTechnicalFieldsToShow([
        'platform',
        'risktier',
        'opensource',
        'aimodeltypes',
        'technicaldocument',
        'environment',
        'lifecyclestage',
      ]);
    }
    if (thirdPartyType === 5) {
      setTechnicalFieldsToShow([
        'risktier',
        'opensource',
        'technicaldocument',
        'watermarkingtechnique',
        'selectcapability',
      ]);
    }
    if (thirdPartyType === 6) {
      setTechnicalFieldsToShow([
        'platform',
        'risktier',
        'opensource',
        'aimodeltypes',
        'technicaldocument',
        'vendorname',
      ]);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state) {
      const {
        internalType: _internalType,
        thirdPartyType: _thirdPartyType,
        sourceType: _sourceType,
      } = location.state;
      setInternalType(_internalType);
      setThirdPartyType(_thirdPartyType);
      setSourceType(_sourceType);
    }
  }, [location.state]);

  useEffect(() => {
    if (systemDetail) {
      loadSourceType();
    }
  }, [systemDetail]);

  useEffect(() => {
    if (location.state) {
      const { systemDetail: _systemDetail } = location.state;
      setSystemDetail(_systemDetail);
    }
  }, [location.state, aiSystemId]);

  return (
    <>
      <PageHeader
        title={purpose === 'new' ? 'Add new AI solution' : 'Edit AI solution'}
        description={
          purpose === 'new'
            ? 'Start by selecting whether this AI solution was developed in-house or provided by a third-party vendor. This helps us classify it correctly and apply the right governance controls.'
            : 'Update the details of an existing AI solution.'
        }
      />
      <Box className="ai-inventory ai-solutions">
        <HeadTag title="AI Systems" />
        <Formik
          initialValues={
            purpose === 'new'
              ? {
                  solutionName: '',
                  solutionOwner: '',
                  organizationUnit: '',
                  organizationSubUnit: '',
                  stakeholder: '',
                  legalentity: '',
                  usercustomer: '',
                  businessprocess: '',
                  uses: '',
                  purpose: '',
                  businessValue: '',
                  platform: '',
                  risktier: 'Low',
                  technicaldocument: '',
                  vendorname: '',
                  consumtionmethod: '',
                  environment: '',
                  versioncontrol: '',
                  selectcapability: '',
                  watermarkingtechnique: '',
                  lifecyclestage: '',
                  trainingdate: '',
                  builddate: '',
                  businessGoals: '',
                  opensource: [false, true],
                  aimodeltypes: [],
                  organizationId: '',
                }
              : {
                  solutionName: systemDetail?.solutionName,
                  solutionOwner: systemDetail?.solutionOwner,
                  organizationUnit: systemDetail?.organizationUnit,
                  organizationSubUnit: systemDetail?.organizationSubUnit,
                  stakeholder: systemDetail?.stakeholder,
                  legalentity: systemDetail?.legalEntity,
                  usercustomer: systemDetail?.userCustomerName,
                  businessprocess: systemDetail?.businessProcesses,
                  uses: systemDetail?.uses,
                  purpose: systemDetail?.purpose,
                  businessValue: systemDetail?.businessValue,
                  platform: systemDetail?.platformName,
                  risktier: systemDetail?.riskTier,
                  technicaldocument: systemDetail?.technicalDocumentationLink,
                  vendorname: systemDetail?.vendorName,
                  consumtionmethod: systemDetail?.consumptionMethod,
                  environment: systemDetail?.environment,
                  versioncontrol: systemDetail?.versionControlMethod,
                  selectcapability: systemDetail?.decisionCapability,
                  watermarkingtechnique: systemDetail?.watermarkingTechnique,
                  lifecyclestage: systemDetail?.lifecycleStage,
                  trainingdate: systemDetail?.lastTrainingDate,
                  builddate: systemDetail?.buildDate,
                  businessGoals: systemDetail?.businessGoals,
                  opensource:
                    systemDetail?.openSource === false
                      ? [true, false]
                      : [false, true],
                  aimodeltypes: systemDetail?.aiModelTypes,
                  organizationId: systemDetail?.organizationId,
                }
          }
          validationSchema={AISolutionsScheme}
          enableReinitialize
          onSubmit={handleFormSourceType}
        >
          {({ setFieldValue, handleSubmit, isSubmitting }) => (
            <Box className="ai-solutions form-source-type">
              <ScrollToError />
              {!showChangeType && (
                <Card variant="outlined">
                  <CardHeader
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 2, sm: 0 },
                    }}
                    action={
                      <AppPrimaryButton
                        sx={{
                          mb: { xs: 3, sm: 0 },
                        }}
                        title="Change"
                        startIcon={
                          <SvgIcon
                            component={Change}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        }
                        onClick={() => {
                          if (purpose !== 'new') {
                            setShowChangeType(true);
                            return;
                          }
                          navigation('/ai-systems/add', {
                            state: {
                              sourceType,
                              internalType,
                              thirdPartyType,
                            },
                          });
                        }}
                      />
                    }
                    title="Source Type"
                    subheader={sourceTypeTitle}
                  />
                </Card>
              )}
              {showChangeType && (
                <CardContent
                  sx={{ padding: '20px 0px !important' }}
                  className="ai-inventory ai-solutions"
                >
                  <Box
                    className="ai-solutions add"
                    sx={{ background: 'white', padding: 5 }}
                  >
                    <Stack spacing={4} direction="column">
                      <Box className="source-type-heading">
                        <Typography variant="h4">Select Source Type</Typography>
                        <Typography component="span" variant="body1">
                          Choose how this AI system was created or acquired
                        </Typography>
                      </Box>
                      <Stack direction={match ? 'column' : 'row'} spacing={2}>
                        <Box
                          className="selection-type"
                          component="button"
                          onClick={() => {
                            setSourceType('internal');
                            setInternalType(1);
                          }}
                        >
                          <Stack direction="row" spacing={1}>
                            <AppCheckBox
                              name="internalAI"
                              onChange={() => {
                                setSourceType('internal');
                                setInternalType(1);
                              }}
                              checked={sourceType === 'internal'}
                            />
                            <SvgIcon
                              component={Home}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                            <Typography variant="h6">
                              Internal AI (built in-house)
                            </Typography>
                          </Stack>
                          <Typography variant="body2" component="span">
                            Built in-house by your organisation
                          </Typography>
                        </Box>
                        <Box
                          className="selection-type"
                          component="button"
                          onClick={() => {
                            setSourceType('third-party');
                            setThirdPartyType(1);
                          }}
                        >
                          <Stack direction="row" spacing={1}>
                            <AppCheckBox
                              name="thirdPartyAI"
                              checked={sourceType === 'third-party'}
                              onChange={() => {
                                setSourceType('third-party');
                                setThirdPartyType(1);
                              }}
                            />
                            <SvgIcon
                              component={Globe}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                            <Typography variant="h6">
                              Third-Party AI (external vendors)
                            </Typography>
                          </Stack>
                          <Typography variant="body2" component="span">
                            Provided by an external vendor
                          </Typography>
                        </Box>
                      </Stack>
                      <Box className="select-classification">
                        <Stack direction={match ? 'column' : 'row'} spacing={1}>
                          <Box className="select-heading">
                            <Typography variant="h5">
                              Select Classification
                            </Typography>
                            <Typography component="span" variant="body1">
                              Choose the category that best describes this AI
                              system. The list will update based on your
                              selected source type.
                            </Typography>
                          </Box>
                        </Stack>
                        <Box className="classification-records">
                          {sourceType === 'internal' && (
                            <>
                              {INTERNAL_TYPES.map((row, index) => (
                                <Card
                                  variant="outlined"
                                  onClick={() => {
                                    setInternalType(index + 1);
                                  }}
                                >
                                  <CardHeader
                                    avatar={
                                      <AppCheckBox
                                        onChange={() => {
                                          setInternalType(index + 1);
                                        }}
                                        checked={internalType === index + 1}
                                      />
                                    }
                                    title={row.title}
                                  />
                                  <CardContent>
                                    <CardActionArea
                                      disableTouchRipple
                                      disableRipple
                                      sx={{
                                        '.MuiCardActionArea-focusHighlight': {
                                          background: 'transparent',
                                        },
                                      }}
                                    >
                                      <Typography component="span">
                                        {row.description}
                                      </Typography>
                                    </CardActionArea>
                                  </CardContent>
                                </Card>
                              ))}
                            </>
                          )}
                          {sourceType === 'third-party' && (
                            <>
                              {THIRD_PARTY_TYPES.map((row, index) => (
                                <Card
                                  variant="outlined"
                                  onClick={() => {
                                    setThirdPartyType(index + 1);
                                  }}
                                >
                                  <CardHeader
                                    avatar={
                                      <AppCheckBox
                                        onChange={() => {
                                          setThirdPartyType(index + 1);
                                        }}
                                        checked={thirdPartyType === index + 1}
                                      />
                                    }
                                    title={row.title}
                                  />
                                  <CardContent>
                                    <CardActionArea
                                      disableTouchRipple
                                      disableRipple
                                      sx={{
                                        '.MuiCardActionArea-focusHighlight': {
                                          background: 'transparent',
                                        },
                                      }}
                                    >
                                      <Typography component="span">
                                        {row.description}
                                      </Typography>
                                    </CardActionArea>
                                  </CardContent>
                                </Card>
                              ))}
                            </>
                          )}
                        </Box>
                        <Box className="source-type-navigation">
                          <AppPrimaryButton
                            title="Cancel"
                            onClick={() => {
                              handleCloseChangeType(true);
                            }}
                          />
                          <AppPrimaryButton
                            title="Continue"
                            onClick={() => {
                              handleCloseChangeType(false);
                            }}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              )}

              {!showChangeType && (
                <>
                  <Box
                    className={`source-type business-usage-information mt-4 ${
                      businessUsageStateOpen ? 'open' : 'close'
                    }`}
                  >
                    <Stack
                      direction="row"
                      className="source-type-heading"
                      component="button"
                      onClick={() => {
                        setBusinessUsageOpen(!businessUsageStateOpen);
                      }}
                    >
                      <Typography variant="h4">
                        Business & Usage Information
                      </Typography>
                      <IconButton
                        onClick={() => {
                          setBusinessUsageOpen(!businessUsageStateOpen);
                        }}
                      >
                        <SvgIcon
                          component={ChevronDown}
                          inheritViewBox
                          className={
                            businessUsageStateOpen
                              ? 'iconButtonImage open'
                              : 'iconButtonImage close'
                          }
                          fontSize="inherit"
                          htmlColor="#FFFFFF"
                        />
                      </IconButton>
                    </Stack>
                    <Collapse in={businessUsageStateOpen}>
                      <Box className="source-type-content">
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 2, sm: 6 }}
                          sx={{ width: '100%' }}
                        >
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="solutionName"
                              placeholder="Name of the AI solution"
                              outerLabel
                              label="Solution name*"
                            />
                          </Box>
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="solutionOwner"
                              placeholder="Name of the solution owner"
                              outerLabel
                              label="Solution owner*"
                            />
                          </Box>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 2, sm: 6 }}
                          sx={{ width: '100%' }}
                        >
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="organizationUnit"
                              placeholder="Name of the organisation unit"
                              outerLabel
                              label="Organisation unit*"
                            />
                          </Box>
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="organizationSubUnit"
                              placeholder="Name of the organisation sub unit"
                              outerLabel
                              label="Organisation sub unit*"
                            />
                          </Box>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 2, sm: 6 }}
                          sx={{ width: '100%' }}
                        >
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="stakeholder"
                              placeholder="Name of the Stakeholders"
                              outerLabel
                              label="Stakeholder*"
                            />
                          </Box>
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="legalentity"
                              placeholder="Name of the Legal entity"
                              outerLabel
                              label="Legal entity*"
                            />
                          </Box>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 2, sm: 6 }}
                          sx={{ width: '100%' }}
                        >
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="usercustomer"
                              placeholder="Name of the User / customer"
                              outerLabel
                              label="User / customer*"
                            />
                          </Box>
                          <Box
                            sx={{
                              width: { xs: '100%', sm: '50%' },
                            }}
                          >
                            <AppFormikTextField
                              name="businessprocess"
                              placeholder="Name of the Business processes"
                              outerLabel
                              label="Business processes*"
                            />
                          </Box>
                        </Stack>
                        <AppFormikTextArea
                          name="uses"
                          placeholder="Describe the uses of the AI solution"
                          outerLabel
                          label="Uses*"
                          rows={2}
                        />
                        <AppFormikTextArea
                          name="purpose"
                          placeholder="Describe the purpose of the AI solution"
                          outerLabel
                          label="Purpose*"
                          rows={2}
                        />
                        <AppFormikTextArea
                          name="businessGoals"
                          placeholder="Describe the business goal of the AI solution"
                          outerLabel
                          label="Business Goals*"
                          rows={2}
                        />
                        <AppFormikTextArea
                          name="businessValue"
                          placeholder="Describe the business value of the AI solution"
                          outerLabel
                          label="Business Value*"
                          rows={2}
                        />
                      </Box>
                    </Collapse>
                  </Box>

                  <Box
                    className={`source-type technical-governance-details ${
                      techGovStateOpen ? 'open' : 'close'
                    }`}
                  >
                    <Stack
                      direction="row"
                      className="source-type-heading"
                      component="button"
                      sx={{
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        minWidth: 0,
                      }}
                      onClick={() => {
                        setTechGovOpen(!techGovStateOpen);
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 0.5, sm: 2 }}
                        sx={{
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: { xs: '17px', sm: '20px' },
                            lineHeight: 1.3,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          Technical & Governance Details
                        </Typography>
                        <Chip
                          label="Required to review"
                          sx={{ flexShrink: 0 }}
                        />
                      </Stack>
                      <IconButton
                        onClick={() => {
                          setTechGovOpen(!techGovStateOpen);
                        }}
                      >
                        <SvgIcon
                          component={ChevronDown}
                          inheritViewBox
                          className={
                            techGovStateOpen
                              ? 'iconButtonImage open'
                              : 'iconButtonImage close'
                          }
                          fontSize="inherit"
                          htmlColor="#FFFFFF"
                        />
                      </IconButton>
                    </Stack>
                    <Collapse in={techGovStateOpen}>
                      <Box className="source-type-content">
                        <Grid spacing={4} container>
                          {technicalFieldsToShow.includes('platform') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="platform"
                                placeholder="Name of the platform"
                                outerLabel
                                label="Platform"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('risktier') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikSelect
                                name="risktier"
                                label="Risk tier"
                                outerLabel
                                items={['Low', 'Moderate', 'High', 'Critical']}
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('opensource') && (
                            <Grid xs={12} sm={6} item>
                              <Stack direction="row">
                                <Box className="label-opensource">
                                  <Typography variant="h6">
                                    Open source
                                  </Typography>
                                  <Typography variant="body2">
                                    Is this open source?
                                  </Typography>
                                </Box>
                                <AppFormikButtonStateTiles
                                  variant="outlined"
                                  name="opensource"
                                  size="small"
                                  label=""
                                  multichoice={false}
                                  className="button-tiles-status"
                                  buttons={[
                                    {
                                      value: 'No',
                                      onClick: (e: any) => {
                                        if (
                                          e.target.classList.contains(
                                            'MuiButtonBase-root'
                                          )
                                        ) {
                                          if (
                                            e.target.classList.contains(
                                              'selected'
                                            )
                                          ) {
                                            setFieldValue('opensource', [
                                              false,
                                              true,
                                            ]);
                                          }
                                        } else {
                                          const DomList: DOMTokenList =
                                            e.target.offsetParent.classList;

                                          if (DomList.contains('selected')) {
                                            setFieldValue('opensource', [
                                              false,
                                              true,
                                            ]);
                                          }
                                        }
                                      },
                                    },
                                    {
                                      value: 'Yes',
                                      onClick: (e: any) => {
                                        if (
                                          e.target.classList.contains(
                                            'MuiButtonBase-root'
                                          )
                                        ) {
                                          if (
                                            e.target.classList.contains(
                                              'selected'
                                            )
                                          ) {
                                            setFieldValue('opensource', [
                                              false,
                                              true,
                                            ]);
                                          }
                                        } else {
                                          const DomList: DOMTokenList =
                                            e.target.offsetParent.classList;

                                          if (DomList.contains('selected')) {
                                            setFieldValue('opensource', [
                                              true,
                                              false,
                                            ]);
                                          }
                                        }
                                      },
                                    },
                                  ]}
                                  outerLabel
                                />
                              </Stack>
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('aimodeltypes') && (
                            <Grid xs={12} sm={6} item>
                              <Box className="ai-model-types">
                                <Typography variant="h5">
                                  AI model types
                                </Typography>
                                <Typography variant="body1" component="span">
                                  Optional - select the types of AI models used
                                  in this solution
                                </Typography>
                                <AppFormikSelect
                                  name="aimodeltypes"
                                  outerLabel={false}
                                  placeholder="Select AI model type (optional)"
                                  notEmphasized
                                  notSelectablePlaceholder
                                  multiple
                                  label=""
                                  items={[
                                    'Naïve Bayes',
                                    'K-Nearest Neighbours',
                                    'Decision trees',
                                    'Random forests',
                                    'Convolutional Neural Network (CNN)',
                                    'Recurrent Neural Network (RNN)',
                                  ]}
                                />
                              </Box>
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes(
                            'technicaldocument'
                          ) && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="technicaldocument"
                                placeholder="Link to technical documentation"
                                outerLabel
                                label="Technical documentation link (optional)"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('vendorname') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="vendorname"
                                placeholder="Name of the vendor"
                                outerLabel
                                label="Vendor name (optional)"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes(
                            'consumtionmethod'
                          ) && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="consumtionmethod"
                                placeholder="Consumption method"
                                outerLabel
                                label="Consumption method (optional)"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('environment') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="environment"
                                placeholder="Environment"
                                outerLabel
                                label="Environment (optional)"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes(
                            'watermarkingtechnique'
                          ) && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikSelect
                                name="watermarkingtechnique"
                                className="rework-selection"
                                notched={false}
                                labelId="fix-selection-label"
                                testid="solicitor-dropdown"
                                MenuProps={{
                                  className:
                                    'menu-fix-selection single-fix-rework',
                                }}
                                outerLabel
                                placeholder="Select watermarking technique"
                                notEmphasized
                                notSelectablePlaceholder
                                label="Watermarking technique (optional)"
                                items={[
                                  'C2PA',
                                  'Project Origin',
                                  'Content Authenticity Initiative',
                                  'W3C Verifiable Credentials',
                                  'Open Provenance Model',
                                  'Invisible Watermarking (DCT-based, SVD-based)',
                                  'Visible Watermarking',
                                  'DeepMark',
                                  'Riva Watermarking',
                                  'Steganography-Based Methods',
                                  'Adobe Firefly Watermarking',
                                  'Truepic',
                                  'ZKML (Zero-Knowledge Proofs for ML)',
                                  'Tree-Ring Watermarking',
                                  'Text-based Watermarks',
                                  'Stable Signature',
                                  'Entropy-based Detection',
                                  'Embedding-based Model Fingerprints',
                                  'PDF Digital Signatures',
                                  'Blockchain Anchoring (OriginStamp, Stamp.io)',
                                  'Git Commit Hashes / SBOM',
                                  'Airbyte/Amundsen Lineage Tagging',
                                ]}
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('lifecyclestage') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikSelect
                                name="lifecyclestage"
                                className="rework-selection"
                                notched={false}
                                labelId="fix-selection-label"
                                testid="solicitor-dropdown"
                                MenuProps={{
                                  className:
                                    'menu-fix-selection single-fix-rework',
                                }}
                                outerLabel
                                placeholder="Select a stage"
                                notEmphasized
                                notSelectablePlaceholder
                                label="Lifecycle stage (optional)"
                                items={[
                                  'Design and development',
                                  'Verification and validation',
                                  'Deployment',
                                  'Operation and monitoring',
                                  'Rollback or decommission',
                                ]}
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('versioncontrol') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikTextField
                                name="versioncontrol"
                                placeholder="Version control method"
                                outerLabel
                                label="Version control method (optional)"
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes(
                            'selectcapability'
                          ) && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikSelect
                                name="selectcapability"
                                className="rework-selection"
                                notched={false}
                                labelId="fix-selection-label"
                                testid="solicitor-dropdown"
                                MenuProps={{
                                  className:
                                    'menu-fix-selection single-fix-rework',
                                }}
                                outerLabel
                                placeholder="Select a capability"
                                notEmphasized
                                notSelectablePlaceholder
                                label="Decision capability (optional)"
                                items={[
                                  'Automated decision-making with static AI/ML models',
                                  'Dynamic AI/ML decision-making',
                                  'Semi-autonomous (with a human-in-the-loop)',
                                  'Fully autonomous',
                                  'No decision-making capability',
                                ]}
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('builddate') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikDatePicker
                                autoFocus
                                name="builddate"
                                testid="datepicker-dropdown"
                                InputProps={{
                                  name: 'builddate',
                                  'aria-labelledby': 'builddate-label',
                                  notched: false,
                                }}
                                value={undefined}
                                onChange={() => console.log('ripple')}
                                onChangeDate={() => console.log('ripple')}
                                onAccepted={() => {
                                  console.log('ripple');
                                }}
                                onClose={() => {
                                  console.log('ripple');
                                }}
                                label="Build date (optional)"
                                outerLabel
                                placeholder="gg/mm/aaaa"
                                renderInput={(props: any) => (
                                  <TextField {...props} />
                                )}
                                autoComplete="off"
                                onEnter={() => {
                                  console.log('ripple');
                                }}
                              />
                            </Grid>
                          )}
                          {technicalFieldsToShow.includes('trainingdate') && (
                            <Grid xs={12} sm={6} item>
                              <AppFormikDatePicker
                                autoFocus
                                name="trainingdate"
                                testid="datepicker-dropdown"
                                InputProps={{
                                  name: 'trainingdate',
                                  'aria-labelledby': 'trainingdate-label',
                                  notched: false,
                                }}
                                value={undefined}
                                onChange={() => console.log('ripple')}
                                onChangeDate={() => console.log('ripple')}
                                onAccepted={() => {
                                  console.log('ripple');
                                }}
                                onClose={() => {
                                  console.log('ripple');
                                }}
                                label="Last training date (optional)"
                                outerLabel
                                placeholder="gg/mm/aaaa"
                                renderInput={(props: any) => (
                                  <TextField {...props} />
                                )}
                                autoComplete="off"
                                onEnter={() => {
                                  console.log('ripple');
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Box>

                  <Box
                    className="source-type-navigation"
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2 },
                      width: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <AppPrimaryButton
                      title="Cancel"
                      onClick={() => {
                        navigation('/ai-systems', {
                          replace: true,
                        });
                      }}
                    />
                    <AppPrimaryButton
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      title={
                        purpose !== 'new'
                          ? 'Update AI Solution'
                          : 'Add AI Solution'
                      }
                      onClick={handleSubmit}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default FormSourceTypes;
